/**
 * MCP Connections Add Approval Dialog
 *
 * Shows a confirmation dialog when an AI agent requests to add a new database
 * connection and the connectionsAddPermission setting is set to 'confirm'.
 */

import { Database, Bot, Clock, Shield } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import type { McpConnectionsAddApprovalRequest } from '../../../types/mcpUiControl';
import { safeServerText } from '../../../types/safeConnectionDisplay';

interface McpConnectionsAddApprovalDialogProps {
  request: McpConnectionsAddApprovalRequest | null;
  onApprove: (requestId: string) => void;
  onDeny: (requestId: string) => void;
}

const ENGINE_LABELS: Record<McpConnectionsAddApprovalRequest['engine'], string> = {
  mssql: 'SQL Server',
  pgsql: 'PostgreSQL',
  mysql: 'MySQL',
  oracle: 'Oracle',
  sqlite: 'SQLite',
};

export function McpConnectionsAddApprovalDialog({
  request,
  onApprove,
  onDeny,
}: McpConnectionsAddApprovalDialogProps) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Set up countdown timer
  useEffect(() => {
    if (!request) {
      setTimeRemaining(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const timeoutMs = request.timeoutMs;
    const endTime = Date.now() + timeoutMs;

    // Initial time remaining
    setTimeRemaining(Math.ceil((endTime - Date.now()) / 1000));

    // Update every second
    timerRef.current = setInterval(() => {
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setTimeRemaining(0);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [request]);

  if (!request) {
    return null;
  }

  const handleApprove = () => {
    onApprove(request.requestId);
  };

  const handleDeny = () => {
    onDeny(request.requestId);
  };

  const engineLabel = ENGINE_LABELS[request.engine] ?? request.engine;

  return (
    <AlertDialog open={!!request} onOpenChange={(open) => !open && handleDeny()}>
      <AlertDialogContent className="max-w-lg" data-testid="add-connection-approval-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500/10">
              <Bot className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <AlertDialogTitle className="text-lg">
                AI agent wants to add a database connection
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1">
                Review the proposed connection details before approving
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* Engine badge */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant="secondary">{engineLabel}</Badge>
          </div>

          {/* Connection details */}
          <div className="rounded-md border border-border bg-muted/50 p-3 space-y-2">
            <div className="flex items-start gap-2">
              <Shield className="h-4 w-4 mt-0.5 flex-shrink-0 text-blue-500" />
              <div className="space-y-1 text-sm">
                <DetailRow label="Alias" value={safeServerText(request.alias)} />
                {request.server && <DetailRow label="Server" value={safeServerText(request.server, request.engine)} />}
                {request.port !== undefined && (
                  <DetailRow label="Port" value={String(request.port)} />
                )}
                {request.login && <DetailRow label="Login" value={request.login} />}
                {request.database && <DetailRow label="Database" value={request.database} />}
              </div>
            </div>
          </div>

          {/* Info note */}
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Database className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              Approving will save this connection to your connection list.
            </span>
          </div>

          {/* Timeout warning */}
          {timeRemaining !== null && timeRemaining <= 30 && (
            <div className="flex items-center gap-2 text-xs text-warning">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {timeRemaining > 0
                  ? `Request expires in ${timeRemaining}s (will be denied)`
                  : 'Request expired'}
              </span>
            </div>
          )}
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleDeny}
            data-testid="add-connection-approval-deny"
          >
            Deny
          </Button>
          <Button
            variant="default"
            onClick={handleApprove}
            data-testid="add-connection-approval-approve"
            disabled={timeRemaining === 0}
          >
            Approve
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="text-muted-foreground min-w-[60px]">{label}:</span>
      <span className="font-mono text-xs break-all">{value}</span>
    </div>
  );
}
